import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { DataProvider, useDataContext } from "./DataContext";
import { FilterProvider, useFilterContext } from "./FilterContext";
import { UIProvider, useUIContext } from "./UIContext";
import { FavoritesProvider, useFavoritesContext } from "./FavoritesContext";
import { hasActiveFilters, buildFetchParams } from "./helpers/filterHelpers";
import { useCatalogueData } from "./hooks/useCatalogueData";
import { useCatalogueFilters } from "./hooks/useCatalogueFilters";
import { useCatalogueNavigation } from "./hooks/useCatalogueNavigation";
import { useCatalogueUI } from "./hooks/useCatalogueUI";
import { catalogueCoordinator } from "./services/catalogueCoordinator";

/**
 * Combined hook for backward compatibility that provides access to all learning catalogue contexts
 * @returns {Object} Combined state and actions from all contexts
 */
export const useLearningCatalogue = () => {
  const dataContext = useDataContext();
  const filterContext = useFilterContext();
  const uiContext = useUIContext();
  const favoritesContext = useFavoritesContext();

  const catalogueData = useCatalogueData();
  const catalogueFilters = useCatalogueFilters();
  const catalogueNavigation = useCatalogueNavigation();
  const catalogueUI = useCatalogueUI();

  const state = useMemo(() => {
    const f = filterContext.filters || {};
    const hasActive = hasActiveFilters(f);

    return {
      ...dataContext.data,
      ...filterContext.filters,
      ...uiContext.uiState,
      favourites: favoritesContext.favorites,
      page: uiContext.uiState.page,
      pageSize: uiContext.uiState.pageSize,
      startDate: f.startDate,
      endDate: f.endDate,
      sessionRegion: f.sessionRegion,
      sessionCity: f.sessionCity,
      hasActiveFilters: hasActive,
      isPublic: dataContext.isPublic,
      allUniqueRegions: dataContext.allUniqueRegions ?? [],
      allUniqueCities: dataContext.allUniqueCities ?? [],
    };
  }, [
    dataContext.data,
    dataContext.isPublic,
    dataContext.allUniqueRegions,
    dataContext.allUniqueCities,
    filterContext.filters,
    uiContext.uiState,
    favoritesContext.favorites,
  ]);

  /**
   * Initializes all data by fetching catalogue data and categories
   */
  const initializeData = useCallback(async () => {
    await catalogueCoordinator.initializeData({
      filterContext,
      uiContext,
      dataContext,
    });
  }, [dataContext, filterContext, uiContext]);

  /**
   * Clears cache and reloads catalogue data from endpoint with current filters
   */
  const reloadFromEndpoint = useCallback(async () => {
    const params = buildFetchParams(
      filterContext.filters,
      uiContext.uiState.page || 1,
    );
    await dataContext.refreshCatalogueData(params);
  }, [dataContext, filterContext.filters, uiContext.uiState.page]);

  /**
   * Fetches catalogue data with filtering and pagination
   * Supports both options object and legacy parameter style for backward compatibility
   * @param {import('./types/fetchOptions').FetchCatalogueDataOptions|string} optionsOrSearchInput - Options object or search input string (legacy)
   * @param {number} [category1] - First category filter ID (legacy)
   * @param {number} [category2] - Second category filter ID (legacy)
   * @param {number} [category3] - Third category filter ID (legacy)
   * @param {number} [category4] - Fourth category filter ID (legacy)
   * @param {number} [category5] - Fifth category filter ID (legacy)
   * @param {number} [pageNum] - Page number for pagination (legacy)
   * @param {number} [sortOrderParam] - Sort order parameter (legacy)
   * @param {string} [startDate] - Start date filter (legacy)
   * @param {string} [endDate] - End date filter (legacy)
   * @param {string} [sessionRegion] - Session region filter (legacy)
   * @param {string} [sessionCity] - Session city filter (legacy)
   * @returns {Promise<Array>} Promise resolving to array of courses
   */
  const fetchCatalogueData = useCallback(
    async (
      optionsOrSearchInput = {},
      category1 = 0,
      category2 = 0,
      category3 = 0,
      category4 = 0,
      category5 = 0,
      pageNum = 1,
      sortOrderParam = 0,
      startDate = null,
      endDate = null,
      sessionRegion = "",
      sessionCity = "",
    ) => {
      const options =
        typeof optionsOrSearchInput === "object" &&
        optionsOrSearchInput !== null
          ? optionsOrSearchInput
          : {
              searchInput: optionsOrSearchInput,
              category1,
              category2,
              category3,
              category4,
              category5,
              pageNum,
              sortOrderParam,
              startDate,
              endDate,
              sessionRegion,
              sessionCity,
            };
      return catalogueData.fetchCatalogueData(options);
    },
    [catalogueData],
  );

  return {
    state,
    toggleListView: catalogueUI.toggleListView,
    toggleDetailsView: catalogueUI.toggleDetailsView,
    handleShowAdvancedFilter: catalogueUI.handleShowAdvancedFilter,
    setPage: catalogueUI.setPage,
    fetchCatalogueData,
    reloadFromEndpoint,
    filterLearnings: catalogueFilters.filterLearnings,
    setSortOrder: catalogueFilters.setSortOrder,
    setSearchText: catalogueFilters.setSearchText,
    setCategoriesValue: catalogueFilters.setCategoriesValue,
    resetFilters: catalogueFilters.resetFilters,
    updateFilters: catalogueFilters.updateFilters,
    navigateToNextCourse: catalogueNavigation.navigateToNextCourse,
    navigateToPreviousCourse: catalogueNavigation.navigateToPreviousCourse,
    isFirstCourse: catalogueNavigation.isFirstCourse,
    isLastCourse: catalogueNavigation.isLastCourse,
    initializeData,
    toggleFavorite: favoritesContext.toggleFavorite,
    isFavorite: favoritesContext.isFavorite,
    getCoursesForCurrentPage: catalogueUI.getCoursesForCurrentPage,
  };
};

/**
 * Main provider component that combines all context providers
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.public - Whether to use public endpoint (default: false)
 * @returns {React.ReactElement} LearningCatalogueProvider component
 */
const LearningCatalogueProvider = ({ children, public: isPublic = false }) => {
  return (
    <DataProvider public={isPublic}>
      <FilterProvider>
        <UIProvider public={isPublic}>
          <FavoritesProvider>{children}</FavoritesProvider>
        </UIProvider>
      </FilterProvider>
    </DataProvider>
  );
};

LearningCatalogueProvider.propTypes = {
  children: PropTypes.node.isRequired,
  public: PropTypes.bool,
};

export default LearningCatalogueProvider;
